import React, { PropsWithChildren, useState } from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import AssignmentIcon from '@mui/icons-material/Assignment';
import WarningIcon from '@mui/icons-material/Warning';
import { ConfirmationDialog } from '@/shared/components/ui';
import { useDeleteEmaQuestionnaire } from '@/features/questionnaires/api';
import { EmaQuestionnaire } from '@aphp-ema/common/questionnaire';
import type { CrossQuestionnaireError } from '../utils/questionnaireErrors';

interface QuestionnaireCardProps {
  title: string;
  questionnaire: EmaQuestionnaire;
  onEdit: () => void;
  onDelete: () => void;
  formLoading?: boolean;
  errors?: CrossQuestionnaireError[];
}

export const QuestionnaireCard: React.FC<QuestionnaireCardProps> = ({
  title,
  questionnaire,
  onEdit,
  onDelete,
  formLoading = false,
  errors = [],
}) => {
  const { t } = useTranslation();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const deleteEmaQuestionnaire = useDeleteEmaQuestionnaire();

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (questionnaire.id) {
      deleteEmaQuestionnaire.mutate(questionnaire.id, {
        onSuccess: () => {
          setDeleteDialogOpen(false);
          onDelete();
        },
        onError: () => {
          setDeleteDialogOpen(false);
        },
      });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const hasErrors = errors.length > 0;

  return (
    <QuestionnaireCardContainer title={title} hasErrors={hasErrors}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {t('pages.studies.emaQuestionnaire.item.fields.title', {
          title: questionnaire.title,
        })}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={onEdit}
          disabled={formLoading}
        >
          {t('buttons.edit')}
        </Button>
        <Button
          variant="outlined"
          color="error"
          size="small"
          onClick={handleDeleteClick}
          disabled={formLoading}
        >
          {t('buttons.delete')}
        </Button>
        {hasErrors && <QuestionnaireErrors errors={errors} />}
      </Box>
      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t('pages.studies.emaQuestionnaire.delete.confirmTitle')}
        message={t('pages.studies.emaQuestionnaire.delete.confirmMessage')}
        confirmText={t('buttons.yes')}
        cancelText={t('buttons.no')}
        loading={deleteEmaQuestionnaire.isLoading}
      />
    </QuestionnaireCardContainer>
  );
};

export const EmptyQuestionnaireCard = ({
  onCreate,
  formLoading,
}: {
  onCreate: () => void;
  formLoading: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <QuestionnaireCardContainer
      title={t('pages.studies.emaQuestionnaire.placeholder.title')}
      hasErrors={false}
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {t('pages.studies.emaQuestionnaire.placeholder.description')}
      </Typography>
      <Button
        variant="contained"
        size="small"
        onClick={onCreate}
        disabled={formLoading}
      >
        {t('buttons.createEmaQuestionnaire')}
      </Button>
    </QuestionnaireCardContainer>
  );
};

const QuestionnaireCardContainer = ({
  children,
  title,
  hasErrors,
}: PropsWithChildren<{
  title: string;
  hasErrors: boolean;
}>) => {
  return (
    <Paper
      sx={{
        p: 2,
        border: hasErrors ? '1px solid #d32f2f' : '1px dashed grey',
        borderRadius: 1,
        position: 'relative',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <AssignmentIcon sx={{ mr: 1, color: 'text.secondary' }} />
        <Typography variant="subtitle1" fontWeight="medium" sx={{ flex: 1 }}>
          {title}
        </Typography>
      </Box>
      <Box>{children}</Box>
    </Paper>
  );
};

const QuestionnaireErrors = ({
  errors,
}: {
  errors: CrossQuestionnaireError[];
}) => {
  const { t } = useTranslation();

  return (
    <Box
      component="span"
      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
    >
      <WarningIcon sx={{ fontSize: 16, color: 'error.main' }} />
      <Typography
        component="span"
        variant="caption"
        sx={{ color: 'error.main', fontWeight: 500 }}
      >
        {t('pages.studies.questions.conditions.invalidConditionsLabel', {
          count: errors.length,
        })}
      </Typography>
    </Box>
  );
};
